import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion, AnimatePresence } from 'motion/react';
import { useToast } from '@/hooks/use-toast';
import {
  ChevronLeft,
  ChevronRight,
  Palette,
  FileText,
  Edit3,
  CheckCircle,
  Presentation,
  Clock,
  BarChart3,
  Eye,
  ArrowRight,
  Calendar,
  User,
  Monitor,
  Play,
  Save,
  Trash2,
  ExternalLink,
  Library } from
'lucide-react';

// Import individual step components
import TemplateSelector from './TemplateSelector';
import ContentPlanner from './ContentPlanner';
import ContentEditor from './ContentEditor';
import ContentApproval from './ContentApproval';
import DesignApplicator from './DesignApplicator';

interface PresentationData {
  title: string;
  subject: string;
  gradeLevel: string;
  template: any;
  content: any[];
  approvedContent: any[];
  finalPresentation: any;
}

interface SavedPresentation {
  id: number;
  teacher_id: number;
  title: string;
  description: string;
  subject: string;
  grade_level: string;
  slides_data: string;
  template_theme: string;
  total_slides: number;
  created_at: string;
  updated_at: string;
}

const STEPS = [
{
  id: 'template',
  label: 'Template Selection',
  icon: Palette,
  description: 'Choose visual themes and color schemes',
  color: 'from-purple-500 to-purple-600'
},
{
  id: 'planning',
  label: 'Content Planning',
  icon: FileText,
  description: 'AI generates content-only structure',
  color: 'from-blue-500 to-blue-600'
},
{
  id: 'editing',
  label: 'Content Editing',
  icon: Edit3,
  description: 'Individual slide editing with AI regeneration',
  color: 'from-green-500 to-green-600'
},
{
  id: 'approval',
  label: 'Content Approval',
  icon: CheckCircle,
  description: 'Review and approve final content',
  color: 'from-orange-500 to-orange-600'
},
{
  id: 'design',
  label: 'Generate Presentation',
  icon: Presentation,
  description: 'Apply design layouts and create final presentation',
  color: 'from-indigo-500 to-indigo-600'
}];


const PresentationGenerator = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [currentStep, setCurrentStep] = useState(0);
  const [presentationData, setPresentationData] = useState<PresentationData>({
    title: '',
    subject: '',
    gradeLevel: '',
    template: null,
    content: [],
    approvedContent: [],
    finalPresentation: null
  });

  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [savedPresentations, setSavedPresentations] = useState<SavedPresentation[]>([]);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  const [fullscreenPresentation, setFullscreenPresentation] = useState<SavedPresentation | null>(null);

  const { toast } = useToast();

  const currentStepData = STEPS[currentStep];
  const progressPercentage = (currentStep + 1) / STEPS.length * 100;

  // Fetch saved presentations
  const fetchSavedPresentations = async () => {
    setIsLoadingSaved(true);
    try {
      const { data, error } = await window.ezsite.apis.tablePage(15255, {
        PageNo: 1,
        PageSize: 50,
        OrderByField: 'updated_at',
        IsAsc: false,
        Filters: []
      });

      if (error) throw error;
      setSavedPresentations(data?.List || []);
    } catch (error) {
      console.error('Error fetching presentations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load saved presentations',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingSaved(false);
    }
  };

  // Save current presentation
  const savePresentation = async () => {
    if (!presentationData.title || !presentationData.finalPresentation) {
      toast({
        title: 'Cannot Save',
        description: 'Please complete the presentation before saving',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      const slidesData = JSON.stringify(presentationData.finalPresentation);

      const { error } = await window.ezsite.apis.tableCreate(15255, {
        teacher_id: 1, // This should come from auth context
        title: presentationData.title,
        description: presentationData.subject,
        subject: presentationData.subject,
        grade_level: presentationData.gradeLevel,
        slides_data: slidesData,
        template_theme: presentationData.template?.name || 'modern',
        total_slides: presentationData.content.length,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Presentation saved successfully!'
      });

      // Refresh saved presentations if on that tab
      if (activeTab === 'saved') {
        fetchSavedPresentations();
      }
    } catch (error) {
      console.error('Error saving presentation:', error);
      toast({
        title: 'Error',
        description: 'Failed to save presentation',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Delete presentation
  const deletePresentation = async (id: number) => {
    try {
      const { error } = await window.ezsite.apis.tableDelete(15255, { ID: id });
      if (error) throw error;

      setSavedPresentations((prev) => prev.filter((p) => p.id !== id));
      toast({
        title: 'Success',
        description: 'Presentation deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting presentation:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete presentation',
        variant: 'destructive'
      });
    }
  };

  // Present in fullscreen
  const presentFullscreen = (presentation: SavedPresentation) => {
    setFullscreenPresentation(presentation);
  };

  useEffect(() => {
    if (activeTab === 'saved') {
      fetchSavedPresentations();
    }
  }, [activeTab]);

  const markStepCompleted = (stepIndex: number) => {
    setCompletedSteps((prev) => new Set([...prev, stepIndex]));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      markStepCompleted(currentStep);
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Allow navigation to completed steps or current step
    if (stepIndex <= currentStep || completedSteps.has(stepIndex)) {
      setCurrentStep(stepIndex);
    }
  };

  const updatePresentationData = (updates: Partial<PresentationData>) => {
    setPresentationData((prev) => ({ ...prev, ...updates }));
  };

  const renderStepContent = () => {
    const stepProps = {
      presentationData,
      updatePresentationData,
      onNext: handleNext,
      onPrevious: handlePrevious,
      isLoading,
      setIsLoading
    };

    switch (STEPS[currentStep].id) {
      case 'template':
        return <TemplateSelector {...stepProps} data-id="mshda3dgk" data-path="src/components/ai/PresentationGenerator.tsx" />;
      case 'planning':
        return <ContentPlanner {...stepProps} data-id="ius1t98mh" data-path="src/components/ai/PresentationGenerator.tsx" />;
      case 'editing':
        return <ContentEditor {...stepProps} data-id="7gl3dandb" data-path="src/components/ai/PresentationGenerator.tsx" />;
      case 'approval':
        return <ContentApproval {...stepProps} data-id="f2mtr3ak0" data-path="src/components/ai/PresentationGenerator.tsx" />;
      case 'design':
        return <DesignApplicator {...stepProps} data-id="vhm0c006s" data-path="src/components/ai/PresentationGenerator.tsx" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fullscreen Presentation Component
  const FullscreenPresentation = ({ presentation, onClose }: {presentation: SavedPresentation;onClose: () => void;}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const slides = JSON.parse(presentation.slides_data);

    const nextSlide = () => {
      if (currentSlide < slides.length - 1) {
        setCurrentSlide(currentSlide + 1);
      }
    };

    const previousSlide = () => {
      if (currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      }
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') nextSlide();
      if (e.key === 'ArrowLeft') previousSlide();
      if (e.key === 'Escape') onClose();
    };

    useEffect(() => {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }, [currentSlide]);

    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col" data-id="73967v13u" data-path="src/components/ai/PresentationGenerator.tsx">
        {/* Header */}
        <div className="bg-gray-900 text-white p-4 flex justify-between items-center" data-id="hze5fdobz" data-path="src/components/ai/PresentationGenerator.tsx">
          <div className="flex items-center gap-4" data-id="mqpk46idb" data-path="src/components/ai/PresentationGenerator.tsx">
            <h1 className="text-xl font-bold" data-id="bzdrh0mgw" data-path="src/components/ai/PresentationGenerator.tsx">{presentation.title}</h1>
            <Badge variant="secondary" data-id="c6adzh7f0" data-path="src/components/ai/PresentationGenerator.tsx">{currentSlide + 1} / {slides.length}</Badge>
          </div>
          <div className="flex items-center gap-2" data-id="qacv28h55" data-path="src/components/ai/PresentationGenerator.tsx">
            <Button
              variant="ghost"
              size="sm"
              onClick={previousSlide}
              disabled={currentSlide === 0}
              className="text-white hover:bg-gray-700" data-id="gaf4if6tu" data-path="src/components/ai/PresentationGenerator.tsx">

              <ChevronLeft className="w-4 h-4" data-id="gu44x9rrl" data-path="src/components/ai/PresentationGenerator.tsx" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="text-white hover:bg-gray-700" data-id="8fgm10635" data-path="src/components/ai/PresentationGenerator.tsx">

              <ChevronRight className="w-4 h-4" data-id="mggk44dwn" data-path="src/components/ai/PresentationGenerator.tsx" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-gray-700" data-id="654wcpwmy" data-path="src/components/ai/PresentationGenerator.tsx">

              <ExternalLink className="w-4 h-4" data-id="eefndi6o3" data-path="src/components/ai/PresentationGenerator.tsx" />
            </Button>
          </div>
        </div>

        {/* Slide Content */}
        <div className="flex-1 flex items-center justify-center p-8" data-id="s8smdrbpl" data-path="src/components/ai/PresentationGenerator.tsx">
          <div className="w-full max-w-6xl bg-white rounded-lg shadow-2xl aspect-video p-12" data-id="zpvc23nva" data-path="src/components/ai/PresentationGenerator.tsx">
            <div className="h-full flex flex-col justify-center" data-id="zu8axca00" data-path="src/components/ai/PresentationGenerator.tsx">
              {slides[currentSlide]?.title &&
              <h1 className="text-5xl font-bold mb-8 text-gray-800" data-id="asa2tlb3i" data-path="src/components/ai/PresentationGenerator.tsx">
                  {slides[currentSlide].title}
                </h1>
              }
              {slides[currentSlide]?.content &&
              <div className="text-2xl text-gray-600 leading-relaxed" data-id="ftrlsoe5k" data-path="src/components/ai/PresentationGenerator.tsx">
                  {Array.isArray(slides[currentSlide].content) ?
                <ul className="space-y-4" data-id="paampt6wo" data-path="src/components/ai/PresentationGenerator.tsx">
                      {slides[currentSlide].content.map((item: string, index: number) =>
                  <li key={index} className="flex items-start gap-3" data-id="l9ddmqacu" data-path="src/components/ai/PresentationGenerator.tsx">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-3 flex-shrink-0" data-id="kvv05kigt" data-path="src/components/ai/PresentationGenerator.tsx" />
                          <span data-id="4u5refsgt" data-path="src/components/ai/PresentationGenerator.tsx">{item}</span>
                        </li>
                  )}
                    </ul> :

                <p data-id="709uglfej" data-path="src/components/ai/PresentationGenerator.tsx">{slides[currentSlide].content}</p>
                }
                </div>
              }
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-900 text-white p-4 text-center" data-id="sd7rwsbjo" data-path="src/components/ai/PresentationGenerator.tsx">
          <p className="text-sm text-gray-400" data-id="mr69voxph" data-path="src/components/ai/PresentationGenerator.tsx">
            Use arrow keys or space to navigate • Press ESC to exit
          </p>
        </div>
      </div>);

  };

  if (fullscreenPresentation) {
    return (
      <FullscreenPresentation
        presentation={fullscreenPresentation}
        onClose={() => setFullscreenPresentation(null)} data-id="658b8d7km" data-path="src/components/ai/PresentationGenerator.tsx" />);


  }

  return (
    <div className="max-w-7xl mx-auto p-6" data-id="4nei14n0o" data-path="src/components/ai/PresentationGenerator.tsx">
      {/* Header */}
      <div className="mb-8" data-id="z5vhr44oy" data-path="src/components/ai/PresentationGenerator.tsx">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center" data-id="5dldkut12" data-path="src/components/ai/PresentationGenerator.tsx">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4" data-id="a61cka4om" data-path="src/components/ai/PresentationGenerator.tsx">
            AI Presentation Generator
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="lhcem1t0s" data-path="src/components/ai/PresentationGenerator.tsx">
            Create professional presentations with our content-first, multi-step workflow. 
            Design beautiful slides powered by AI with website-style layouts.
          </p>
        </motion.div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="1dc2vusi0" data-path="src/components/ai/PresentationGenerator.tsx">
        <TabsList className="grid w-full grid-cols-2 mb-8" data-id="q3tj6zgfm" data-path="src/components/ai/PresentationGenerator.tsx">
          <TabsTrigger value="create" className="flex items-center gap-2" data-id="g4rshdwfv" data-path="src/components/ai/PresentationGenerator.tsx">
            <Presentation className="w-4 h-4" data-id="x5123z7xj" data-path="src/components/ai/PresentationGenerator.tsx" />
            Create New
          </TabsTrigger>
          <TabsTrigger value="saved" className="flex items-center gap-2" data-id="72ealcuno" data-path="src/components/ai/PresentationGenerator.tsx">
            <Library className="w-4 h-4" data-id="xojig6ouy" data-path="src/components/ai/PresentationGenerator.tsx" />
            Saved Presentations
          </TabsTrigger>
        </TabsList>

        {/* Create Presentation Tab */}
        <TabsContent value="create" className="space-y-6" data-id="oucndu17j" data-path="src/components/ai/PresentationGenerator.tsx">
          {/* Progress Bar */}
          <div data-id="vur7sl1u2" data-path="src/components/ai/PresentationGenerator.tsx">
            <div className="flex items-center justify-between mb-4" data-id="waeuh38qr" data-path="src/components/ai/PresentationGenerator.tsx">
              <span className="text-sm font-medium text-gray-700" data-id="8iy046lat" data-path="src/components/ai/PresentationGenerator.tsx">
                Step {currentStep + 1} of {STEPS.length}
              </span>
              <span className="text-sm text-gray-500" data-id="kvyat7n0r" data-path="src/components/ai/PresentationGenerator.tsx">
                {Math.round(progressPercentage)}% Complete
              </span>
            </div>
            <Progress value={progressPercentage} className="h-3" data-id="v4571dvqw" data-path="src/components/ai/PresentationGenerator.tsx" />
          </div>

          {/* Step Navigation */}
          <div data-id="e6i797hdl" data-path="src/components/ai/PresentationGenerator.tsx">
            <div className="flex items-center justify-between" data-id="p3y4j9ffl" data-path="src/components/ai/PresentationGenerator.tsx">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isCompleted = completedSteps.has(index);
                const isCurrent = index === currentStep;
                const isAccessible = index <= currentStep || completedSteps.has(index);

                return (
                  <motion.div
                    key={step.id}
                    className="flex flex-col items-center flex-1"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }} data-id="qauzxt315" data-path="src/components/ai/PresentationGenerator.tsx">
                    <Button
                      variant={isCurrent ? "default" : isCompleted ? "secondary" : "outline"}
                      size="lg"
                      className={`
                        relative w-16 h-16 rounded-full mb-3 transition-all duration-300
                        ${isCurrent ? `bg-gradient-to-r ${step.color} text-white hover:shadow-lg transform hover:scale-105` : ''}
                        ${isCompleted ? 'bg-green-100 text-green-700 border-green-300' : ''}
                        ${!isAccessible ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      onClick={() => isAccessible && handleStepClick(index)}
                      disabled={!isAccessible} data-id="oov3lv4ge" data-path="src/components/ai/PresentationGenerator.tsx">
                      {isCompleted ?
                      <CheckCircle className="w-6 h-6" data-id="8bqcribgz" data-path="src/components/ai/PresentationGenerator.tsx" /> :
                      <Icon className="w-6 h-6" data-id="pm6hejd7h" data-path="src/components/ai/PresentationGenerator.tsx" />
                      }
                      {isCurrent &&
                      <motion.div
                        className="absolute inset-0 rounded-full border-4 border-white/30"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }} data-id="v0uw08rw0" data-path="src/components/ai/PresentationGenerator.tsx" />
                      }
                    </Button>
                    
                    <div className="text-center max-w-24" data-id="xsn8bqio1" data-path="src/components/ai/PresentationGenerator.tsx">
                      <h3 className={`text-sm font-medium mb-1 ${
                      isCurrent ? 'text-indigo-600' :
                      isCompleted ? 'text-green-600' :
                      'text-gray-500'}`} data-id="2bj5cj3jc" data-path="src/components/ai/PresentationGenerator.tsx">
                        {step.label}
                      </h3>
                      <p className="text-xs text-gray-400 hidden md:block leading-tight" data-id="emwi3g1s5" data-path="src/components/ai/PresentationGenerator.tsx">
                        {step.description}
                      </p>
                    </div>
                    
                    {index < STEPS.length - 1 &&
                    <div className="hidden md:block absolute top-8 left-1/2 transform translate-x-8 w-full h-0.5 bg-gray-200" data-id="y7k1idtg7" data-path="src/components/ai/PresentationGenerator.tsx">
                        <div
                        className={`h-full transition-all duration-500 ${
                        isCompleted ? 'bg-green-400' : 'bg-gray-200'}`}
                        style={{ width: isCompleted ? '100%' : '0%' }} data-id="r8lt90n3s" data-path="src/components/ai/PresentationGenerator.tsx" />
                      </div>
                    }
                  </motion.div>);
              })}
            </div>
          </div>

          {/* Step Content */}
          <Card className="min-h-[600px]" data-id="1696t7rtp" data-path="src/components/ai/PresentationGenerator.tsx">
            <CardHeader data-id="r7ys1txs1" data-path="src/components/ai/PresentationGenerator.tsx">
              <div className="flex items-center justify-between" data-id="koj2rrv55" data-path="src/components/ai/PresentationGenerator.tsx">
                <div data-id="mtgxr6aa3" data-path="src/components/ai/PresentationGenerator.tsx">
                  <CardTitle className="flex items-center gap-3" data-id="l8zgxi42h" data-path="src/components/ai/PresentationGenerator.tsx">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${currentStepData.color} text-white`} data-id="on1n51zcy" data-path="src/components/ai/PresentationGenerator.tsx">
                      <currentStepData.icon className="w-5 h-5" data-id="hcwp9sylk" data-path="src/components/ai/PresentationGenerator.tsx" />
                    </div>
                    {currentStepData.label}
                  </CardTitle>
                  <p className="text-gray-600 mt-2" data-id="kuc1mfgqb" data-path="src/components/ai/PresentationGenerator.tsx">{currentStepData.description}</p>
                </div>
                
                {/* Step Indicators */}
                <div className="flex items-center gap-2" data-id="deoxi3tq2" data-path="src/components/ai/PresentationGenerator.tsx">
                  {presentationData.title &&
                  <Badge variant="outline" className="text-xs" data-id="n5n81sui7" data-path="src/components/ai/PresentationGenerator.tsx">
                      <FileText className="w-3 h-3 mr-1" data-id="lj9aw7697" data-path="src/components/ai/PresentationGenerator.tsx" />
                      {presentationData.title}
                    </Badge>
                  }
                  {presentationData.content.length > 0 &&
                  <Badge variant="outline" className="text-xs" data-id="4z74d21n3" data-path="src/components/ai/PresentationGenerator.tsx">
                      <BarChart3 className="w-3 h-3 mr-1" data-id="mhokd58eg" data-path="src/components/ai/PresentationGenerator.tsx" />
                      {presentationData.content.length} slides
                    </Badge>
                  }
                  {presentationData.finalPresentation &&
                  <Button
                    onClick={savePresentation}
                    disabled={isLoading}
                    size="sm"
                    className="text-xs" data-id="kmiv05mn6" data-path="src/components/ai/PresentationGenerator.tsx">
                      <Save className="w-3 h-3 mr-1" data-id="m838fg4xe" data-path="src/components/ai/PresentationGenerator.tsx" />
                      Save
                    </Button>
                  }
                </div>
              </div>
            </CardHeader>
            
            <CardContent data-id="4fo7m29qr" data-path="src/components/ai/PresentationGenerator.tsx">
              <AnimatePresence mode="wait" data-id="5cvcutdk0" data-path="src/components/ai/PresentationGenerator.tsx">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }} data-id="90w0zu8jc" data-path="src/components/ai/PresentationGenerator.tsx">
                  {renderStepContent()}
                </motion.div>
              </AnimatePresence>
            </CardContent>
          </Card>

          {/* Navigation Footer */}
          <div className="flex items-center justify-between" data-id="szk4bwytq" data-path="src/components/ai/PresentationGenerator.tsx">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex items-center gap-2" data-id="7ssezdigq" data-path="src/components/ai/PresentationGenerator.tsx">
              <ChevronLeft className="w-4 h-4" data-id="suz1jd436" data-path="src/components/ai/PresentationGenerator.tsx" />
              Previous
            </Button>

            <div className="flex items-center gap-4" data-id="te359q7p9" data-path="src/components/ai/PresentationGenerator.tsx">
              {/* Step Info */}
              <div className="text-sm text-gray-500 text-center" data-id="lcg3kj9kw" data-path="src/components/ai/PresentationGenerator.tsx">
                <p data-id="xtjro2fsp" data-path="src/components/ai/PresentationGenerator.tsx">Step {currentStep + 1} of {STEPS.length}</p>
                <p className="text-xs mt-1" data-id="m5zdfh26b" data-path="src/components/ai/PresentationGenerator.tsx">
                  {completedSteps.size} of {STEPS.length} steps completed
                </p>
              </div>
            </div>

            <Button
              onClick={handleNext}
              disabled={currentStep === STEPS.length - 1}
              className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700" data-id="txwnr3jow" data-path="src/components/ai/PresentationGenerator.tsx">
              Next
              <ChevronRight className="w-4 h-4" data-id="4potx76vh" data-path="src/components/ai/PresentationGenerator.tsx" />
            </Button>
          </div>

          {/* Quick Stats */}
          {presentationData.content.length > 0 &&
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="n66jlzvtn" data-path="src/components/ai/PresentationGenerator.tsx">
              <Card className="p-4" data-id="7l168jtnd" data-path="src/components/ai/PresentationGenerator.tsx">
                <div className="flex items-center gap-3" data-id="piorp86vm" data-path="src/components/ai/PresentationGenerator.tsx">
                  <div className="p-2 bg-blue-100 rounded-lg" data-id="mh2h29un1" data-path="src/components/ai/PresentationGenerator.tsx">
                    <FileText className="w-5 h-5 text-blue-600" data-id="o6tgeks09" data-path="src/components/ai/PresentationGenerator.tsx" />
                  </div>
                  <div data-id="w2h9axx44" data-path="src/components/ai/PresentationGenerator.tsx">
                    <p className="text-sm text-gray-600" data-id="rvy1t297b" data-path="src/components/ai/PresentationGenerator.tsx">Total Slides</p>
                    <p className="text-2xl font-bold text-blue-600" data-id="gpsuxpwnr" data-path="src/components/ai/PresentationGenerator.tsx">{presentationData.content.length}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4" data-id="i23lphl8q" data-path="src/components/ai/PresentationGenerator.tsx">
                <div className="flex items-center gap-3" data-id="275op9tn1" data-path="src/components/ai/PresentationGenerator.tsx">
                  <div className="p-2 bg-green-100 rounded-lg" data-id="9xyw0ecr3" data-path="src/components/ai/PresentationGenerator.tsx">
                    <Clock className="w-5 h-5 text-green-600" data-id="9pmv2wwcs" data-path="src/components/ai/PresentationGenerator.tsx" />
                  </div>
                  <div data-id="jq3qow5u4" data-path="src/components/ai/PresentationGenerator.tsx">
                    <p className="text-sm text-gray-600" data-id="h70m6aitk" data-path="src/components/ai/PresentationGenerator.tsx">Est. Reading Time</p>
                    <p className="text-2xl font-bold text-green-600" data-id="0kzj2e0zp" data-path="src/components/ai/PresentationGenerator.tsx">
                      {Math.ceil(presentationData.content.length * 2)} min
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4" data-id="y017ptdat" data-path="src/components/ai/PresentationGenerator.tsx">
                <div className="flex items-center gap-3" data-id="ui946le7g" data-path="src/components/ai/PresentationGenerator.tsx">
                  <div className="p-2 bg-purple-100 rounded-lg" data-id="lvp75599t" data-path="src/components/ai/PresentationGenerator.tsx">
                    <Eye className="w-5 h-5 text-purple-600" data-id="iaht3alkd" data-path="src/components/ai/PresentationGenerator.tsx" />
                  </div>
                  <div data-id="lqmaoggbn" data-path="src/components/ai/PresentationGenerator.tsx">
                    <p className="text-sm text-gray-600" data-id="v3up2p2zr" data-path="src/components/ai/PresentationGenerator.tsx">Progress</p>
                    <p className="text-2xl font-bold text-purple-600" data-id="4vdb7zl2t" data-path="src/components/ai/PresentationGenerator.tsx">{Math.round(progressPercentage)}%</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          }
        </TabsContent>

        {/* Saved Presentations Tab */}
        <TabsContent value="saved" className="space-y-6" data-id="moklvq2r8" data-path="src/components/ai/PresentationGenerator.tsx">
          <div className="flex items-center justify-between" data-id="b6cshs8uj" data-path="src/components/ai/PresentationGenerator.tsx">
            <div data-id="a6udyfecq" data-path="src/components/ai/PresentationGenerator.tsx">
              <h2 className="text-2xl font-bold text-gray-800" data-id="mqql3q7t7" data-path="src/components/ai/PresentationGenerator.tsx">Saved Presentations</h2>
              <p className="text-gray-600" data-id="svza73dj2" data-path="src/components/ai/PresentationGenerator.tsx">View and manage your saved presentations</p>
            </div>
            <Button
              onClick={fetchSavedPresentations}
              disabled={isLoadingSaved}
              variant="outline" data-id="wrplpzlv1" data-path="src/components/ai/PresentationGenerator.tsx">
              Refresh
            </Button>
          </div>

          {isLoadingSaved ?
          <div className="text-center py-12" data-id="1yygcpmbn" data-path="src/components/ai/PresentationGenerator.tsx">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" data-id="wt8ro2c8l" data-path="src/components/ai/PresentationGenerator.tsx"></div>
              <p className="text-gray-600" data-id="l51txrmal" data-path="src/components/ai/PresentationGenerator.tsx">Loading presentations...</p>
            </div> :
          savedPresentations.length === 0 ?
          <Card className="p-12 text-center" data-id="qqevqk7yy" data-path="src/components/ai/PresentationGenerator.tsx">
              <div className="text-gray-400 mb-4" data-id="z9pbq517v" data-path="src/components/ai/PresentationGenerator.tsx">
                <Library className="w-16 h-16 mx-auto" data-id="t6lz0kig0" data-path="src/components/ai/PresentationGenerator.tsx" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2" data-id="z5yfn9yw7" data-path="src/components/ai/PresentationGenerator.tsx">No presentations yet</h3>
              <p className="text-gray-500 mb-6" data-id="xsqspctip" data-path="src/components/ai/PresentationGenerator.tsx">Create your first presentation to see it here</p>
              <Button onClick={() => setActiveTab('create')} data-id="cpjdgj1zl" data-path="src/components/ai/PresentationGenerator.tsx">
                <Presentation className="w-4 h-4 mr-2" data-id="cpctinnkh" data-path="src/components/ai/PresentationGenerator.tsx" />
                Create New Presentation
              </Button>
            </Card> :

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="qsomusndj" data-path="src/components/ai/PresentationGenerator.tsx">
              {savedPresentations.map((presentation) =>
            <motion.div
              key={presentation.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }} data-id="cpdgaz7jz" data-path="src/components/ai/PresentationGenerator.tsx">
                  <Card className="h-full flex flex-col" data-id="h3ppxhiet" data-path="src/components/ai/PresentationGenerator.tsx">
                    <CardHeader data-id="xrw6vex54" data-path="src/components/ai/PresentationGenerator.tsx">
                      <div className="flex items-start justify-between" data-id="exu8yck5h" data-path="src/components/ai/PresentationGenerator.tsx">
                        <div data-id="spslq625z" data-path="src/components/ai/PresentationGenerator.tsx">
                          <CardTitle className="text-lg mb-2" data-id="7mf4yr7cc" data-path="src/components/ai/PresentationGenerator.tsx">{presentation.title}</CardTitle>
                          <div className="flex items-center gap-2 mb-2" data-id="sf9yznvin" data-path="src/components/ai/PresentationGenerator.tsx">
                            <Badge variant="secondary" className="text-xs" data-id="rqpn39qwb" data-path="src/components/ai/PresentationGenerator.tsx">
                              {presentation.subject}
                            </Badge>
                            <Badge variant="outline" className="text-xs" data-id="ylxhlu2c3" data-path="src/components/ai/PresentationGenerator.tsx">
                              {presentation.grade_level}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2" data-id="3k6ele1b1" data-path="src/components/ai/PresentationGenerator.tsx">
                        {presentation.description}
                      </p>
                    </CardHeader>
                    
                    <CardContent className="flex-1 flex flex-col justify-between" data-id="7008xtl96" data-path="src/components/ai/PresentationGenerator.tsx">
                      <div className="space-y-3 mb-4" data-id="li5hxstv7" data-path="src/components/ai/PresentationGenerator.tsx">
                        <div className="flex items-center gap-2 text-sm text-gray-500" data-id="xqs9jhi4a" data-path="src/components/ai/PresentationGenerator.tsx">
                          <BarChart3 className="w-4 h-4" data-id="c9acv8u8c" data-path="src/components/ai/PresentationGenerator.tsx" />
                          {presentation.total_slides} slides
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500" data-id="i90imd103" data-path="src/components/ai/PresentationGenerator.tsx">
                          <Calendar className="w-4 h-4" data-id="zjct3qhrv" data-path="src/components/ai/PresentationGenerator.tsx" />
                          {formatDate(presentation.updated_at)}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500" data-id="m2bw9s0tw" data-path="src/components/ai/PresentationGenerator.tsx">
                          <Palette className="w-4 h-4" data-id="g442drdrx" data-path="src/components/ai/PresentationGenerator.tsx" />
                          {presentation.template_theme}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2" data-id="6djn335ru" data-path="src/components/ai/PresentationGenerator.tsx">
                        <Button
                      onClick={() => presentFullscreen(presentation)}
                      className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700" data-id="55pl5l22f" data-path="src/components/ai/PresentationGenerator.tsx">
                          <Play className="w-4 h-4 mr-2" data-id="iloperj91" data-path="src/components/ai/PresentationGenerator.tsx" />
                          Present
                        </Button>
                        <Button
                      onClick={() => deletePresentation(presentation.id)}
                      variant="outline"
                      size="icon"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="ktc3d7je2" data-path="src/components/ai/PresentationGenerator.tsx">
                          <Trash2 className="w-4 h-4" data-id="p0qgr8bhr" data-path="src/components/ai/PresentationGenerator.tsx" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
            )}
            </div>
          }
        </TabsContent>
      </Tabs>
    </div>);

};

export default PresentationGenerator;